"use client";

import { useEffect, useRef } from "react";
import { installComponentMonitoring } from "./component";
import type { ComponentRenderMonitorDefinition } from "./component";
import { installMonitoring } from "./core/runtime";
import type { InstallMonitoringOptions, PodMonitoringSchema } from "./core/types";
import { installInteractionMonitoring } from "./interaction";
import type { InteractionMonitoringConfig } from "./interaction";

export interface MonitoringRuntimeProps {
  schema: PodMonitoringSchema;
  componentMonitors?: ComponentRenderMonitorDefinition[];
  interactionOptions?: InteractionMonitoringConfig;
  observeFetch?: InstallMonitoringOptions["observeFetch"];
  observeXhr?: InstallMonitoringOptions["observeXhr"];
  observePrompt?: InstallMonitoringOptions["observePrompt"];
}

export function MonitoringRuntime({
  schema,
  componentMonitors,
  interactionOptions,
  observeFetch,
  observeXhr,
  observePrompt,
}: MonitoringRuntimeProps) {
  const initialPropsRef = useRef<MonitoringRuntimeProps | null>(null);

  if (initialPropsRef.current === null) {
    initialPropsRef.current = {
      schema,
      componentMonitors,
      interactionOptions,
      observeFetch,
      observeXhr,
      observePrompt,
    };
  }

  useEffect(() => {
    const initialProps = initialPropsRef.current ?? {
      schema,
      componentMonitors,
      interactionOptions,
      observeFetch,
      observeXhr,
      observePrompt,
    };
    const {
      schema: initialSchema,
      componentMonitors: initialComponentMonitors,
      interactionOptions: initialInteractionOptions,
      observeFetch: initialObserveFetch,
      observeXhr: initialObserveXhr,
      observePrompt: initialObservePrompt,
    } = initialProps;

    const cleanupMonitoring = installMonitoring({
      schema: initialSchema,
      ...(initialObserveFetch === undefined ? {} : { observeFetch: initialObserveFetch }),
      ...(initialObserveXhr === undefined ? {} : { observeXhr: initialObserveXhr }),
      ...(initialObservePrompt === undefined ? {} : { observePrompt: initialObservePrompt }),
    });

    const cleanupInteractionMonitoring = installInteractionMonitoring({
      schema: initialSchema,
      ...initialInteractionOptions,
    });

    const cleanupComponentMonitoring = initialComponentMonitors?.length
      ? installComponentMonitoring({
          schema: initialSchema,
          components: initialComponentMonitors,
        })
      : () => undefined;

    return () => {
      cleanupComponentMonitoring();
      cleanupInteractionMonitoring();
      cleanupMonitoring();
    };
  }, []);

  return null;
}
